import type {
  ArrAnchor,
  ArrExternalBenchmark,
  ArrSegmentAssumption,
  LabCompany,
  PlatformMonthlyEvidence,
} from "../dataSources/tokenArrEvidence";

type ArrModelId = "driver-index" | "log-trend" | "net-new-velocity" | "last-reported";

export interface LabArrScenario {
  codingPaidUserMultiplier: number;
  b2bApiAnnualGrowthPct: number;
  enterpriseSeatMultiplier: number;
  effectivePriceMultiplier: number;
  computeCapacityMultiplier: number;
}

interface ArrBacktestPoint {
  observedAt: string;
  availableAt: string;
  actualArrB: number;
  predictedArrB: number;
  errorB: number;
  absoluteErrorB: number;
  absolutePercentageError: number;
  trainingAnchors: number;
  horizonMonths: number;
}

interface ArrBacktestModel {
  id: ArrModelId;
  label: string;
  points: ArrBacktestPoint[];
  metrics: {
    full: { observations: number; maeB: number; rmseB: number; mape: number; meanErrorB: number; underforecastRatePct: number };
    recent2: { observations: number; maeB: number; rmseB: number; mape: number; meanErrorB: number; underforecastRatePct: number };
  };
}

export interface LabArrCompanyResult {
  company: LabCompany;
  monitor: {
    latestAnchor: ArrAnchor;
    anchorAgeDays: number;
    /** Backwards-compatible alias for latestCompleteMonthNowcastArrB. */
    currentNowcastArrB: number;
    currentMonth: string;
    nowcastIsModelEstimate: boolean;
    latestCompleteMonthNowcastArrB: number;
    latestCompleteMonth: string;
    /** Pure champion-model estimate before applying any current-level research calibration. */
    statisticalCurrentDateNowcastArrB: number;
    /** Headline current ARR level; equals the statistical nowcast when no qualified calibration is available. */
    currentDateNowcastArrB: number;
    currentDate: string;
    currentCalibration: ArrExternalBenchmark | null;
  };
  forecast: {
    selectedModel: ArrModelId;
    status: "research-baseline";
    validatedHorizonMonths: number;
    points: Array<{
      month: string;
      horizon: number;
      totalArrB: number;
      codingB: number;
      b2bApiB: number;
      generalSubscriptionsB: number;
      computeBinding: boolean;
      monthsFromLatestAnchor: number;
      withinBacktestedHorizon: boolean;
    }>;
  };
  backtest: {
    primary: ArrBacktestModel;
    candidates: Record<ArrModelId, ArrBacktestModel>;
    biasDiagnostics: {
      fullWindow: { observations: number; underforecastRatePct: number; meanErrorB: number };
      recentWindow: { observations: number; underforecastRatePct: number; meanErrorB: number };
    };
    skillVsLastReportedPct: number | null;
    promotionGate: { passed: boolean; reason: string; minimumOosAnchors: number };
  };
  anchors: ArrAnchor[];
}

export interface LabArrSliceResult {
  monitor: {
    asOf: string;
    latestCompleteMonth: string;
    combinedLatestReportedArrB: number;
    /** Backwards-compatible alias for combinedLatestCompleteMonthNowcastArrB. */
    combinedNowcastArrB: number;
    combinedLatestCompleteMonthNowcastArrB: number;
    combinedStatisticalCurrentDateNowcastArrB: number;
    combinedCurrentDateNowcastArrB: number;
    /** Analyst estimates may compare or calibrate the current level, but never enter fitting/backtests. */
    externalBenchmarks: ArrExternalBenchmark[];
    companies: Array<{
      company: LabCompany;
      latestReportedArrB: number;
      reportedAt: string;
      availableAt: string;
      /** Backwards-compatible alias for latestCompleteMonthNowcastArrB. */
      nowcastArrB: number;
      latestCompleteMonthNowcastArrB: number;
      statisticalCurrentDateNowcastArrB: number;
      currentDateNowcastArrB: number;
      currentCalibration: ArrExternalBenchmark | null;
      anchorAgeDays: number;
    }>;
  };
  companies: Record<LabCompany, LabArrCompanyResult>;
  backtest: {
    historicalScenarioLocked: true;
    totalOosAnchors: number;
    warning: string;
  };
  methodology: {
    targetDefinition: string;
    formula: string;
    pointInTimeRule: string;
    validation: string;
    computeConstraint: string;
    limitations: string[];
  };
}

const MIN_TRAINING_ANCHORS = 2;
const MIN_COMPANY_OOS_ANCHORS = 2;
const MIN_COMBINED_OOS_ANCHORS = 6;

function monthIndex(date: string) {
  const [year, month] = date.slice(0, 7).split("-").map(Number);
  return year * 12 + month - 1;
}

function monthDistance(start: string, end: string) {
  return Math.max(0, monthIndex(end) - monthIndex(start));
}

function addMonths(month: string, amount: number) {
  const [year, value] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, value - 1 + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function daysBetween(start: string, end: string) {
  return Math.max(0, Math.floor((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000));
}

const AVERAGE_DAYS_PER_MONTH = 365.2425 / 12;

function elapsedMonths(start: string, end: string) {
  return daysBetween(start, end) / AVERAGE_DAYS_PER_MONTH;
}

function monthEnd(month: string) {
  const [year, value] = month.split("-").map(Number);
  return new Date(Date.UTC(year, value, 0)).toISOString().slice(0, 10);
}

function linearLogSlope(anchors: readonly ArrAnchor[]) {
  if (anchors.length < 2) return 0;
  const origin = monthIndex(anchors[0].observedAt);
  const xs = anchors.map((anchor) => monthIndex(anchor.observedAt) - origin);
  const ys = anchors.map((anchor) => Math.log(anchor.arrB));
  const meanX = xs.reduce((sum, value) => sum + value, 0) / xs.length;
  const meanY = ys.reduce((sum, value) => sum + value, 0) / ys.length;
  const denominator = xs.reduce((sum, value) => sum + (value - meanX) ** 2, 0);
  if (!(denominator > 0)) return 0;
  return xs.reduce((sum, value, index) => sum + (value - meanX) * (ys[index] - meanY), 0) / denominator;
}

function median(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function driverValue(company: LabCompany, row: PlatformMonthlyEvidence) {
  const tokens = company === "openai" ? row.openRouterOpenAiTokensT : row.openRouterAnthropicTokensT;
  const downloads = company === "openai" ? row.codexDownloads : row.claudeCodeDownloads;
  return Math.sqrt(Math.max(tokens, 1e-6) * Math.max(downloads, 1));
}

function driverGrowth(company: LabCompany, platform: readonly PlatformMonthlyEvidence[], availableAt: string) {
  const cutoff = availableAt.slice(0, 7);
  const rows = platform.filter((row) => row.month < cutoff).slice(-7);
  const logs: number[] = [];
  for (let index = 1; index < rows.length; index += 1) {
    const prior = driverValue(company, rows[index - 1]);
    const current = driverValue(company, rows[index]);
    if (prior > 0 && current > 0) logs.push(Math.log(current / prior));
  }
  return Math.max(-0.05, Math.min(0.12, median(logs) * 0.55));
}

/**
 * Latest disclosed net-new ARR per month, using observed dates rather than
 * disclosure dates. Callers are responsible for supplying only anchors that
 * were available at the forecast origin; the walk-forward path does this
 * strictly, which prevents later milestones from leaking into earlier fits.
 */
function netNewArrVelocity(history: readonly ArrAnchor[]) {
  const ordered = [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt));
  const latest = ordered.at(-1);
  const prior = ordered.at(-2);
  if (!latest || !prior) return 0;
  const elapsedDays = daysBetween(prior.observedAt, latest.observedAt);
  if (!(elapsedDays > 0)) return 0;
  return (latest.arrB - prior.arrB) / elapsedDays * AVERAGE_DAYS_PER_MONTH;
}

function predictAt(
  id: ArrModelId,
  company: LabCompany,
  history: readonly ArrAnchor[],
  targetObservedAt: string,
  originAvailableAt: string,
  platform: readonly PlatformMonthlyEvidence[],
) {
  const latest = [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt)).at(-1);
  if (!latest) throw new Error(`${company} ARR model has no prior anchor.`);
  // Use the actual point-in-time distance for valuation dates inside a month;
  // otherwise a July 13 nowcast would incorrectly equal the July 31 forecast.
  const horizon = elapsedMonths(latest.observedAt, targetObservedAt);
  if (id === "last-reported") return latest.arrB;
  if (id === "net-new-velocity") {
    return Math.max(0, latest.arrB + netNewArrVelocity(history) * horizon);
  }
  const monthlyGrowth = id === "log-trend"
    ? Math.max(-0.05, Math.min(0.12, linearLogSlope(history)))
    : driverGrowth(company, platform, originAvailableAt);
  return latest.arrB * Math.exp(monthlyGrowth * horizon);
}

function backtest(
  id: ArrModelId,
  company: LabCompany,
  anchors: readonly ArrAnchor[],
  platform: readonly PlatformMonthlyEvidence[],
): ArrBacktestModel {
  const points: ArrBacktestPoint[] = [];
  for (const target of anchors) {
    const history = anchors.filter((anchor) => anchor.availableAt < target.availableAt && anchor.observedAt < target.observedAt);
    if (history.length < MIN_TRAINING_ANCHORS) continue;
    const predictedArrB = predictAt(id, company, history, target.observedAt, target.availableAt, platform);
    const latestTrainingAnchor = [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt)).at(-1)!;
    const errorB = predictedArrB - target.arrB;
    points.push({
      observedAt: target.observedAt,
      availableAt: target.availableAt,
      actualArrB: target.arrB,
      predictedArrB,
      errorB,
      absoluteErrorB: Math.abs(errorB),
      absolutePercentageError: Math.abs(errorB) / target.arrB * 100,
      trainingAnchors: history.length,
      horizonMonths: monthDistance(latestTrainingAnchor.observedAt, target.observedAt),
    });
  }
  const calculate = (rows: ArrBacktestPoint[]) => {
    const observations = rows.length;
    return {
      observations,
      maeB: observations ? rows.reduce((sum, point) => sum + point.absoluteErrorB, 0) / observations : 0,
      rmseB: observations ? Math.sqrt(rows.reduce((sum, point) => sum + point.errorB ** 2, 0) / observations) : 0,
      mape: observations ? rows.reduce((sum, point) => sum + point.absolutePercentageError, 0) / observations : 0,
      meanErrorB: observations ? rows.reduce((sum, point) => sum + point.errorB, 0) / observations : 0,
      underforecastRatePct: observations ? rows.filter((point) => point.errorB < 0).length / observations * 100 : 0,
    };
  };
  return {
    id,
    label: id === "driver-index"
      ? "Provider Token + coding adoption driver"
      : id === "log-trend"
        ? "Point-in-time log trend"
        : id === "net-new-velocity"
          ? "Point-in-time latest net-new ARR velocity"
          : "Last reported ARR",
    points,
    metrics: { full: calculate(points), recent2: calculate(points.slice(-2)) },
  };
}

function validateScenario(scenario: LabArrScenario) {
  for (const [key, value] of Object.entries(scenario)) if (!Number.isFinite(value)) throw new Error(`Lab ARR scenario ${key} must be finite.`);
  if (scenario.codingPaidUserMultiplier <= 0 || scenario.enterpriseSeatMultiplier <= 0
    || scenario.effectivePriceMultiplier <= 0 || scenario.computeCapacityMultiplier <= 0) {
    throw new Error("Lab ARR scenario multipliers must be positive.");
  }
  if (scenario.b2bApiAnnualGrowthPct <= -100) throw new Error("B2B API annual growth must be greater than -100%.");
}

function companyResult(args: {
  company: LabCompany;
  asOf: string;
  latestCompleteMonth: string;
  anchors: readonly ArrAnchor[];
  platform: readonly PlatformMonthlyEvidence[];
  segments: ArrSegmentAssumption;
  externalBenchmarks: readonly ArrExternalBenchmark[];
  scenario: LabArrScenario;
}): LabArrCompanyResult {
  const visible = args.anchors.filter((anchor) => anchor.company === args.company && anchor.availableAt <= args.asOf)
    .sort((left, right) => left.availableAt.localeCompare(right.availableAt) || left.observedAt.localeCompare(right.observedAt));
  const eligible = visible.filter((anchor) => anchor.backtestEligible);
  if (eligible.length < 3) throw new Error(`${args.company} has insufficient point-in-time ARR anchors.`);
  const latest = [...visible].sort((left, right) => left.observedAt.localeCompare(right.observedAt)).at(-1)!;
  const candidates = {
    "driver-index": backtest("driver-index", args.company, eligible, args.platform),
    "log-trend": backtest("log-trend", args.company, eligible, args.platform),
    "net-new-velocity": backtest("net-new-velocity", args.company, eligible, args.platform),
    "last-reported": backtest("last-reported", args.company, eligible, args.platform),
  } satisfies Record<ArrModelId, ArrBacktestModel>;
  const baseline = candidates["last-reported"];
  const challengers = [candidates["driver-index"], candidates["log-trend"], candidates["net-new-velocity"]].filter((model) =>
    model.metrics.full.observations >= MIN_COMPANY_OOS_ANCHORS
    && model.metrics.full.rmseB <= baseline.metrics.full.rmseB
    && model.metrics.recent2.rmseB <= baseline.metrics.recent2.rmseB);
  const selected = challengers.sort((left, right) => left.metrics.full.rmseB - right.metrics.full.rmseB)[0] ?? baseline;
  const validatedHorizonMonths = Math.max(...selected.points.map((point) => point.horizonMonths));
  const completeMonthEnd = monthEnd(args.latestCompleteMonth);
  const latestCompleteMonthNowcastArrB = predictAt(
    selected.id,
    args.company,
    eligible,
    completeMonthEnd,
    args.asOf,
    args.platform,
  );
  const statisticalCurrentDateNowcastArrB = predictAt(
    selected.id,
    args.company,
    eligible,
    args.asOf,
    args.asOf,
    args.platform,
  );
  const currentCalibration = args.externalBenchmarks
    .filter((benchmark) => benchmark.scope === args.company
      && benchmark.calibrationEligible
      && benchmark.publishedAt <= args.asOf
      && benchmark.asOf <= args.asOf)
    .sort((left, right) => left.asOf.localeCompare(right.asOf) || left.publishedAt.localeCompare(right.publishedAt))
    .at(-1) ?? null;
  const statisticalAtCalibration = currentCalibration
    ? predictAt(selected.id, args.company, eligible, currentCalibration.asOf, args.asOf, args.platform)
    : null;
  const calibrateLevel = (statisticalValue: number) => {
    if (!currentCalibration || statisticalAtCalibration == null || !(statisticalAtCalibration > 0)) return statisticalValue;
    return selected.id === "net-new-velocity"
      ? Math.max(0, currentCalibration.valueB + statisticalValue - statisticalAtCalibration)
      : Math.max(0, currentCalibration.valueB * statisticalValue / statisticalAtCalibration);
  };
  const currentDateNowcastArrB = calibrateLevel(statisticalCurrentDateNowcastArrB);
  const sinceAnchor = monthDistance(latest.observedAt, completeMonthEnd);
  const points: LabArrCompanyResult["forecast"]["points"] = [];
  for (let horizon = 1; horizon <= 12; horizon += 1) {
    const forecastMonth = addMonths(args.latestCompleteMonth, horizon);
    const statisticalBaseTotal = predictAt(
      selected.id,
      args.company,
      eligible,
      monthEnd(forecastMonth),
      args.asOf,
      args.platform,
    );
    const baseTotal = calibrateLevel(statisticalBaseTotal);
    const baseScale = baseTotal / args.segments.anchorArrB;
    const price = args.scenario.effectivePriceMultiplier;
    const codingB = args.segments.codingB * baseScale * args.scenario.codingPaidUserMultiplier * price;
    const b2bApiB = args.segments.b2bApiB * baseScale
      * (1 + args.scenario.b2bApiAnnualGrowthPct / 100) ** (horizon / 12) * price;
    const generalSubscriptionsB = args.segments.generalSubscriptionsB * baseScale
      * args.scenario.enterpriseSeatMultiplier * price;
    const capacityScale = Math.min(1, args.scenario.computeCapacityMultiplier);
    const monthsFromLatestAnchor = sinceAnchor + horizon;
    points.push({
      month: forecastMonth,
      horizon,
      totalArrB: (codingB + b2bApiB + generalSubscriptionsB) * capacityScale,
      codingB: codingB * capacityScale,
      b2bApiB: b2bApiB * capacityScale,
      generalSubscriptionsB: generalSubscriptionsB * capacityScale,
      computeBinding: args.scenario.computeCapacityMultiplier < 1,
      monthsFromLatestAnchor,
      withinBacktestedHorizon: monthsFromLatestAnchor <= validatedHorizonMonths,
    });
  }
  const skill = baseline.metrics.full.rmseB > 0 ? (1 - selected.metrics.full.rmseB / baseline.metrics.full.rmseB) * 100 : null;
  return {
    company: args.company,
    monitor: {
      latestAnchor: latest,
      anchorAgeDays: daysBetween(latest.availableAt, args.asOf),
      currentNowcastArrB: latestCompleteMonthNowcastArrB,
      currentMonth: args.latestCompleteMonth,
      nowcastIsModelEstimate: args.latestCompleteMonth > latest.observedAt.slice(0, 7),
      latestCompleteMonthNowcastArrB,
      latestCompleteMonth: args.latestCompleteMonth,
      statisticalCurrentDateNowcastArrB,
      currentDateNowcastArrB,
      currentDate: args.asOf,
      currentCalibration,
    },
    forecast: { selectedModel: selected.id, status: "research-baseline", validatedHorizonMonths, points },
    backtest: {
      primary: selected,
      candidates,
      biasDiagnostics: {
        fullWindow: {
          observations: selected.metrics.full.observations,
          underforecastRatePct: selected.metrics.full.underforecastRatePct,
          meanErrorB: selected.metrics.full.meanErrorB,
        },
        recentWindow: {
          observations: selected.metrics.recent2.observations,
          underforecastRatePct: selected.metrics.recent2.underforecastRatePct,
          meanErrorB: selected.metrics.recent2.meanErrorB,
        },
      },
      skillVsLastReportedPct: skill,
      promotionGate: {
        passed: selected.id !== "last-reported",
        reason: selected.id !== "last-reported"
          ? "The challenger beat last-reported ARR on all available point-in-time OOS anchors."
          : "No challenger beat last-reported ARR; the model correctly retains the simple baseline.",
        minimumOosAnchors: MIN_COMPANY_OOS_ANCHORS,
      },
    },
    anchors: visible,
  };
}

export function runLabArrSlice(args: {
  asOf: string;
  latestCompleteMonth: string;
  anchors: readonly ArrAnchor[];
  platformMonthly: readonly PlatformMonthlyEvidence[];
  segments: Record<LabCompany, ArrSegmentAssumption>;
  externalBenchmarks?: readonly ArrExternalBenchmark[];
  scenario: LabArrScenario;
}): LabArrSliceResult {
  validateScenario(args.scenario);
  const externalBenchmarks = (args.externalBenchmarks ?? [])
    .filter((benchmark) => benchmark.benchmarkEligible && benchmark.publishedAt <= args.asOf)
    .map((benchmark) => ({ ...benchmark }));
  const openai = companyResult({ ...args, company: "openai", platform: args.platformMonthly, segments: args.segments.openai, externalBenchmarks });
  const anthropic = companyResult({ ...args, company: "anthropic", platform: args.platformMonthly, segments: args.segments.anthropic, externalBenchmarks });
  const companies = { openai, anthropic };
  const totalOosAnchors = openai.backtest.primary.metrics.full.observations + anthropic.backtest.primary.metrics.full.observations;
  return {
    monitor: {
      asOf: args.asOf,
      latestCompleteMonth: args.latestCompleteMonth,
      combinedLatestReportedArrB: openai.monitor.latestAnchor.arrB + anthropic.monitor.latestAnchor.arrB,
      combinedNowcastArrB: openai.monitor.currentNowcastArrB + anthropic.monitor.currentNowcastArrB,
      combinedLatestCompleteMonthNowcastArrB: openai.monitor.latestCompleteMonthNowcastArrB
        + anthropic.monitor.latestCompleteMonthNowcastArrB,
      combinedStatisticalCurrentDateNowcastArrB: openai.monitor.statisticalCurrentDateNowcastArrB
        + anthropic.monitor.statisticalCurrentDateNowcastArrB,
      combinedCurrentDateNowcastArrB: openai.monitor.currentDateNowcastArrB
        + anthropic.monitor.currentDateNowcastArrB,
      externalBenchmarks,
      companies: ([openai, anthropic] as const).map((company) => ({
        company: company.company,
        latestReportedArrB: company.monitor.latestAnchor.arrB,
        reportedAt: company.monitor.latestAnchor.observedAt,
        availableAt: company.monitor.latestAnchor.availableAt,
        nowcastArrB: company.monitor.currentNowcastArrB,
        latestCompleteMonthNowcastArrB: company.monitor.latestCompleteMonthNowcastArrB,
        statisticalCurrentDateNowcastArrB: company.monitor.statisticalCurrentDateNowcastArrB,
        currentDateNowcastArrB: company.monitor.currentDateNowcastArrB,
        currentCalibration: company.monitor.currentCalibration,
        anchorAgeDays: company.monitor.anchorAgeDays,
      })),
    },
    companies,
    backtest: {
      historicalScenarioLocked: true,
      totalOosAnchors,
      warning: totalOosAnchors >= MIN_COMBINED_OOS_ANCHORS
        ? "The combined minimum OOS count is met, but private-company run-rate definitions and segment allocations keep this slice at research-baseline."
        : `Only ${totalOosAnchors}/${MIN_COMBINED_OOS_ANCHORS} combined OOS anchors are available; retain the last-reported baseline.`,
    },
    methodology: {
      targetDefinition: "OpenAI and Anthropic disclosed or credibly reported annualized revenue run-rate; this is not GAAP revenue.",
      formula: "ARR = coding plans + B2B API/enterprise + general subscriptions. Champion models determine growth; an explicitly qualified company-level external point may rebase the current level without rewriting history, after which segment scenarios reconcile to the calibrated total.",
      pointInTimeRule: "At every walk-forward origin, fitting uses only anchors whose availableAt is strictly earlier than the target disclosure date and only complete driver months then available.",
      validation: "Each company challenger needs at least two OOS milestone forecasts and no worse full/recent-two RMSE than last-reported ARR; full/recent bias diagnostics report underforecast rate and signed mean error. The combined evidence warning requires at least six OOS anchors.",
      computeConstraint: "Compute is a cap on served/monetized demand. It is never added as revenue and binds only when the scenario multiplier is below 1.",
      limitations: [
        "ARR, annualized revenue run-rate, and GAAP revenue are not interchangeable.",
        "Milestone dates and definitions are irregular, so backtest sample sizes remain small.",
        "OpenRouter and npm are scoped adoption drivers, not company revenue ledgers.",
        "External analyst estimates are never training anchors. Explicit company-level point estimates may calibrate the current level, while public aggregate/lower-bound benchmarks remain comparison overlays only.",
        "Segment allocations are explicit model assumptions and should be replaced when audited company disclosures become available.",
      ],
    },
  };
}
